import axios from "axios";
import pako from "pako";
import {
    ENDPOINT_DISCARD_GEN,
    ENDPOINT_DOWNLOAD_GEN,
    ENDPOINT_GEN,
    ENDPOINT_RETRIEVE_GEN,
    ENDPOINT_RETRIEVE_GEN_META,
    ENDPOINT_STATUS_GEN,
    ENDPOINT_XML_LIBRARY,
    ENDPOINT_SAVE_GEN_TO_LIBRARY, // New endpoint for saving
    ENDPOINT_CANCEL_GEN
} from "../../constants";
import { GeneratedData, GeneratedResponse, STATUS_SAVED, STATUS_SAVING, STATUS_UNSAVED, XMLLibraryEntry, XMLMeta, TypeData } from "./store"; // Import XMLLibraryEntry
import type { DownloadFilters } from "./downloadFilters";

export async function generateFromCutoff(cutoff: string): Promise<string> {
    const res = await axios.get(ENDPOINT_GEN + "?cutoff="+cutoff, {withCredentials:true});
    console.log(res.data)
    return res.data.Code
}

export async function retrieveGenerated() : Promise<GeneratedResponse> {
    const res = await axios.get(ENDPOINT_RETRIEVE_GEN, {withCredentials:true});
    //console.log( res)
    
    // Parse the string response as JSON first
    let data: any;
    try {
        // The response comes as a string that needs to be parsed
        data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
    } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        throw new Error("Invalid response format from server");
    }
    
    if (data.Code !== "200" && data.Code !== STATUS_SAVED && data.Code !== STATUS_UNSAVED && data.Code !== STATUS_SAVING) 
    {
        return data
    }

    const summaryPayload = data.Summary ?? null;

    // Decompress the base64 encoded gzipped data
    try {
        // Decode base64 to binary
        const binaryString = atob(data.Msg);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Decompress with pako (gzip)
        const decompressed = pako.ungzip(bytes, { to: 'string' });

        // Parse the decompressed JSON string
        const payload = JSON.parse(decompressed);
        data.Msg = payload;
    } catch (error) {
        console.error("Failed to decompress and parse data:", error);
        throw new Error("Failed to process generated data");
    }
    data.Summary = summaryPayload;
    
    return data
}

export async function retrieveGeneratedMeta(filename?: string): Promise<XMLMeta | null> {
    const query = filename ? `?filename=${encodeURIComponent(filename)}` : "";
    const res = await axios.get(`${ENDPOINT_RETRIEVE_GEN_META}${query}`, { withCredentials: true });

    if (res.data.Code !== 200) {
        throw new Error(res.data.Msg || "Failed to retrieve generated metadata");
    }

    let payload: any;
    try {
        payload = typeof res.data.Msg === "string" ? JSON.parse(res.data.Msg) : res.data.Msg;
    } catch (err) {
        console.error("Failed to parse metadata response:", err);
        throw new Error("Invalid metadata response format");
    }

    return (payload?.meta ?? null) as XMLMeta | null;
}

export async function retrieveStatus() {
    const res = await axios.get(ENDPOINT_STATUS_GEN,{withCredentials:true });
    if (res.data.Code !== 200 )
        throw new Error(res.data.Msg)
    //console.log( res.data.Msg)
    return JSON.parse( res.data.Msg)
}

export async function downloadGenerated(raw:boolean, cutoff?: string, filename?: string, format?: string, submissionType: number = -1, filters?: DownloadFilters, previewFilename?: string) : Promise<any>{
    let endpoint = ENDPOINT_DOWNLOAD_GEN + "?"
    // for raw file we don't need filename. use the company id as filename
    if (raw) filename = ""

    // Add filename parameter if provided
    if (cutoff) {
        endpoint += "cutoff=" + encodeURIComponent(cutoff) + "&"
    }
    if (filename) {
        endpoint += "filename=" + encodeURIComponent(filename) + "&"
    }
    if (format) {
        endpoint += "format=" + encodeURIComponent(format) + "&"
    }
    if (previewFilename) {
        endpoint += "previewFilename=" + encodeURIComponent(previewFilename) + "&"
    }
    endpoint += "submissionType=" + encodeURIComponent(String(submissionType)) + "&"
    if (filters?.excludeContractStatuses?.length) {
        filters.excludeContractStatuses.forEach(status => {
            const normalized = status.toUpperCase()
            endpoint += "excludeStatuses=" + encodeURIComponent(normalized) + "&"
        })
    }
    
    if (!raw) 
        endpoint += "raw=0&"
    else  
        endpoint += "raw=1&"
    endpoint += "rand=" + Date.now()

    // window.open(endpoint)
    // return new Promise<string>((resolve, reject) => {})
    return axios.get(endpoint, {
        url: endpoint,
        method: "GET",
        responseType:'blob',
        withCredentials: true});
}

export async function discardGen() : Promise<any> {
    const res = await axios.get(ENDPOINT_DISCARD_GEN,{withCredentials: true});
    if (res.data.Code !== 200 )
        throw new Error(res.data.Msg)
    //console.log( res.data)
    return res.data.Msg
}

export async function cancelGeneration() : Promise<any> {
    const res = await axios.get(ENDPOINT_CANCEL_GEN, {withCredentials: true});
    if (res.data.Code !== 200 )
        throw new Error(res.data.Msg)
    return res.data.Msg
}

// --- XML Library Actions ---

export async function listLibraryFiles(): Promise<XMLLibraryEntry[]> {
    // Use ENDPOINT_XML_LIBRARY which should point to /v1/xml/library
    const res = await axios.get(ENDPOINT_XML_LIBRARY, { withCredentials: true });
    console.log(res.data)
    if (res.data.Code !== 200) {
        throw new Error(res.data.Msg || "Failed to list library files");
    }
    // Assuming the backend returns the list directly in res.data.Msg or similar structure
    // Adjust parsing based on actual backend response structure if needed
    // If HttpSuccessObj sends the object directly in data:
    if (Array.isArray(res.data)) {
         return res.data as XMLLibraryEntry[];
    }
    // If HttpSuccessObj wraps it:
    if (res.data.Msg && Array.isArray(res.data.Msg)) {
        return res.data.Msg as XMLLibraryEntry[];
    }
    // Fallback or specific parsing if needed
    try {
        // Attempt to parse if it's a stringified JSON array
        return JSON.parse(res.data.Msg || "[]") as XMLLibraryEntry[];
    } catch (e) {
        console.error("Could not parse library file list:", res.data);
        throw new Error("Received invalid data format for library files.");
    }
}


export async function getLibraryFile(filename: string): Promise<GeneratedData> {
    // Use ENDPOINT_XML_LIBRARY and append filename and format=json query param
    const endpoint = `${ENDPOINT_XML_LIBRARY}/${encodeURIComponent(filename)}?format=json`;
    const res = await axios.get(endpoint, { withCredentials: true });

    // Check for successful HTTP status first (axios might throw for non-2xx)
    if (res.status !== 200) {
         // If backend sends error details in JSON for non-200 responses handled by axios
         if (res.data && res.data.Msg) {
            throw new Error(res.data.Msg);
         }
         throw new Error(`Failed to get file ${filename}. Status: ${res.status}`);
    }

    // If backend returns JSON directly (not wrapped in Code/Msg for this specific endpoint)
    if (typeof res.data === 'object' && res.data !== null && res.data.Header) {
        return { ...(res.data as GeneratedData) };
    }

    // If backend wraps the JSON data in a Code/Msg structure (like other endpoints)
    if (res.data.Code === 200 && res.data.Msg) {
        try {
            // Assuming Msg contains the stringified JSON of GeneratedData
            const parsed = JSON.parse(res.data.Msg) as GeneratedData;
            return { ...parsed };
        } catch (e) {
            console.error("Could not parse library file data:", res.data.Msg);
            throw new Error("Received invalid data format for library file content.");
        }
    }

    // Handle unexpected response structure
    console.error("Unexpected response structure for getLibraryFile:", res.data);
    throw new Error(res.data.Msg || `Failed to retrieve or parse file ${filename}`);
}

export async function saveGeneratedToLibrary(): Promise<string> {
    // Make a POST request to the save endpoint
    const res = await axios.post(ENDPOINT_SAVE_GEN_TO_LIBRARY, {}, { withCredentials: true });

    // Check the response code from the backend
    if (res.data.Code !== 200) {
        console.error("Save failed:", res.data.Msg);
        throw new Error(res.data.Msg || "Failed to save generated data to library");
    }

    // Return the success code (or message if needed)
    return res.data.Code.toString(); // Assuming Code is numeric, convert to string
}

export async function getLibraryFileHeader(filename: string): Promise<TypeData | null> {
    const endpoint = `${ENDPOINT_XML_LIBRARY}/${encodeURIComponent(filename)}?format=json&headerOnly=true`;
    const res = await axios.get(endpoint, { withCredentials: true });

    if (typeof res.data === 'object' && res.data !== null && res.data.Header) {
        return res.data.Header as TypeData;
    }

    return null;
}

export async function deleteLibraryFile(filename: string): Promise<string> {
    const endpoint = `${ENDPOINT_XML_LIBRARY}/${encodeURIComponent(filename)}`;
    try {
        const res = await axios.delete(endpoint, { withCredentials: true });
        console.log(res.data)
        // Check backend response code if available, otherwise rely on HTTP status
        if (res.data && typeof res.data.Code !== 'undefined') {
            if (res.data.Code === 200 || res.data.Code === 204) {
                console.log(`Successfully deleted ${filename} via API.`);
                return res.data.Code.toString(); // Return success code
            } else {
                // Backend indicated failure despite 2xx HTTP status
                console.error(`API failed to delete ${filename}:`, res.data.Msg);
                throw new Error(res.data.Msg || `Failed to delete file (Code: ${res.data.Code})`);
            }
        } else if (res.status === 200 || res.status === 204) {
             // Handle success based on HTTP status if Code is not present
             console.log(`Successfully deleted ${filename} via API (Status: ${res.status}).`);
             return res.status.toString();
        } else {
             // Handle unexpected success status codes if necessary
             console.warn(`Unexpected success status ${res.status} for delete operation.`);
             throw new Error(`Unexpected status code ${res.status} during deletion.`);
        }
    } catch (error: any) {
        console.error(`Error deleting file ${filename} via API:`, error);
        // Try to extract backend error message if available in error response
        const errorMessage = error.response?.data?.Msg || error.message || "Unknown error during deletion";
        throw new Error(errorMessage); // Rethrow the error to be caught by the store action
    }
}
